// Client-side admin session limits.
//
// There is exactly ONE now: the absolute cap. The admin JWT auto-refreshes for
// as long as a tab is open, so this is the only thing that eventually forces
// re-auth from the client's side.
//
// A 1-hour INACTIVITY limit used to live here too, and it was wrong. It watched
// mousemove/keydown/click/scroll in a single tab and signed the admin out on a
// wall-clock deadline while the server session was still entirely valid --
// verified in prod on 2026-09-07, where a refresh SUCCEEDED one second before
// the timer's sign-out. Worse, because sign-out hands off to central identity
// (see AdminShell), that browser timer also ended the l2u-identity session, so
// one idle tab logged the person out of every portal.
//
// Inactivity now belongs to THIS portal's own Supabase project -- the one
// VITE_SUPABASE_URL points at, which is where every token refresh goes for
// password admins and central-identity users alike. Its Auth > Sessions settings
// hold both real limits (4-hour inactivity, 12-hour timebox; read from prod on
// 2026-09-07). The identity project has windows of its own, but they only decide
// whether an SSO re-entry hop is silent -- they have no say in whether a portal
// session survives, so do not go looking there for this knob.
//
// That window is measured against the SDK's refresh call, which is the honest
// signal: it cannot be defeated by a laptop sleeping, and it is not per-tab.
//
// The value below is deliberately duplicated from that project's timebox rather
// than derived, because nothing in the client can read it. Change both together.
export const ABSOLUTE_MS = 12 * 60 * 60 * 1000; // 12 hours since sign-in

const SESSION_START_KEY = 'l2u-admin-session-start';

export function isAbsoluteExpired(
  sessionStartMs: number,
  nowMs: number,
  maxMs = ABSOLUTE_MS,
): boolean {
  return nowMs - sessionStartMs >= maxMs;
}

// Reasons the portal can end a session for, and therefore explain on /login.
// 'expired' -- the absolute cap -- is the only one left; 'idle' was retired with
// the inactivity limit above.
//
// Codes only. The wording for each lives solely in auth/AdminLogin.tsx, the same
// split denyReason.ts uses, so this module never renders prose.
const SESSION_END_REASONS = ['expired'] as const;

export type SessionEndReason = (typeof SESSION_END_REASONS)[number];

/**
 * Reads the `reason` query parameter /login was sent with.
 *
 * Three outcomes, because there are three genuinely different situations:
 *
 *   null          -- no reason given, so the session did not end on our terms
 *                    and no notice belongs on the page at all.
 *   'expired'     -- a reason we can explain precisely.
 *   'unspecified' -- something ended the session and named a reason we do not
 *                    recognise. The caller must still say SOMETHING, just
 *                    nothing specific.
 *
 * That third case is not hypothetical. A browser still running the bundle that
 * had the idle timer keeps navigating to `/login?reason=idle` for as long as
 * that tab lives, and it would be a lie to answer that with either "your
 * session reached its 12-hour limit" (it did not) or "signed out due to
 * inactivity" (no such limit exists any more).
 *
 * It also fixes an older bug in passing: the notice used to treat every value
 * that was not 'idle' as the 12-hour cap, so `?reason=anything` claimed a limit
 * at random.
 */
export function readSessionEndReason(
  value: string | null | undefined,
): SessionEndReason | 'unspecified' | null {
  if (value === null || value === undefined || value === '') return null;
  return (SESSION_END_REASONS as readonly string[]).includes(value)
    ? (value as SessionEndReason)
    : 'unspecified';
}

export function markSessionStart(nowMs: number): void {
  try {
    localStorage.setItem(SESSION_START_KEY, String(nowMs));
  } catch {
    /* private mode / storage disabled */
  }
}

// The stored anchor, seeding it to nowMs when a live session has none (e.g. a
// session predating this feature) so the absolute cap always has an anchor.
export function ensureSessionStart(nowMs: number): number {
  try {
    const parsed = Number(localStorage.getItem(SESSION_START_KEY));
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  } catch {
    /* ignore */
  }
  markSessionStart(nowMs);
  return nowMs;
}

export function clearSessionStart(): void {
  try {
    localStorage.removeItem(SESSION_START_KEY);
  } catch {
    /* ignore */
  }
}
